import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import FileOpenRoundedIcon from "@mui/icons-material/FileOpenRounded";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Box, Button, Chip, Grid, Modal, Paper, TextField, Accordion, AccordionSummary, AccordionDetails, Tooltip, Typography, styled, Divider, FormControlLabel, Checkbox, CircularProgress } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { fetchToken } from "../auth/Token";
import { useLocation } from 'react-router-dom';
import Avatar from "@mui/material/Avatar";
import { decode } from "base-64";
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  borderStyle: 'groove',
  border: '3px',
  borderColor: 'grey',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.2)',
  marginTop: '10px',  
  marginBottom: '10px',
  minHeight: '50px',
  borderRadius: '10px'
}));

const TeamsCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  paddingTop: 4,
  paddingBottom: 4,
  borderStyle: 'groove',
  border: '3px',
  borderColor: 'grey',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.2)',
  marginTop: '10px',  
  marginBottom: '10px',
  minHeight: '50px',
  borderRadius: '10px'
}));

function CircularProgressWithLabel({ value, performance }) {
  // Determine color based on performance rating
  const getColor = () => {
    switch(performance) {
      case 'Excellent': return '#2ecc71';
      case 'Good': return '#3498db';
      case 'Fair': return '#f39c12';
      case 'Poor': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress 
        variant="determinate" 
        value={value} 
        size={60}
        thickness={4}
        sx={{ color: getColor() }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
          sx={{ fontWeight: 'bold' }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}


// Styled components
const PerformanceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
}));

const PerformanceBar = styled(Box)(({ value, theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: 
    value >= 90 ? theme.palette.success.light :
    value >= 75 ? theme.palette.info.light :
    value >= 50 ? theme.palette.warning.light :
    theme.palette.error.light,
  width: `${value}%`,
  maxWidth: '100%',
}));

const GroupLoanPerformance = ({ groupLoan }) => {

  const currencyFormat = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
  });

  return (
    <Box sx={{ mt: 3 }}>
      {/* Group Summary Section */}
      <Typography 
        sx={{ 
          textDecoration: 'underline',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} 
        variant="h6" 
        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
      >
        <b>Group Performance Summary</b>
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <PerformanceCard elevation={3}>
            <Typography 
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
              sx={{ mb: 1 }}
            >
              <b>Savings Consistency</b>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress 
                variant="determinate" 
                value={groupLoan.group_performance.average_consistency} 
                size={60}
                color={
                  groupLoan.group_performance.average_consistency >= 90 ? 'success' :
                  groupLoan.group_performance.average_consistency >= 75 ? 'info' :
                  groupLoan.group_performance.average_consistency >= 50 ? 'warning' :
                  'error'
                }
              />
              <Box>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{groupLoan.group_performance.average_consistency}%</b>
                </Typography>
                <Chip 
                  label={groupLoan.group_performance.performance_rating}
                  size="small"
                  color={
                    groupLoan.group_performance.performance_rating === 'Excellent' ? 'success' :
                    groupLoan.group_performance.performance_rating === 'Good' ? 'info' :
                    groupLoan.group_performance.performance_rating === 'Fair' ? 'warning' :
                    'error'
                  }
                />
              </Box>
            </Box>
          </PerformanceCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <PerformanceCard elevation={3}>
            <Typography 
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
              sx={{ mb: 1 }}
            >
              <b>Group Composition</b>
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Members: {groupLoan.group_performance.member_count}</b>
                </Typography>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Total Amount: {currencyFormat.format(groupLoan.total_amount)}</b>
                </Typography>
              </Box>
              <Box>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Loan Type: {groupLoan.loan_type}</b>
                </Typography>
                {/* <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Status: {groupLoan.member_signed === 'Yes' ? 'Signed' : 'Pending Signatures'}</b>
                </Typography> */}
              </Box>
            </Box>
          </PerformanceCard>
        </Grid>
      </Grid>

      {/* Member Details Section */}
      <Typography 
        sx={{ 
          textDecoration: 'underline',
          mt: 3,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} 
        variant="h6" 
        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
      >
        <b>Member Performance Details</b>
      </Typography>
      
      {groupLoan.member_performance.map((member, index) => (
        <Accordion key={member.member_id} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{member.member_name}</b>
              </Typography>
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Loan: {currencyFormat.format(member.loan_amount)}</b>
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {member.savings_performance.length > 0 ? (
              member.savings_performance.map((account, accIndex) => (
                <Box key={accIndex} sx={{ mb: 2 }}>
                  <Typography 
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <b>{account.account_name}</b>
                    <Chip 
                      label={`Balance: $${account.current_balance.toLocaleString()}`} 
                      size="small" 
                      variant="outlined" 
                    />
                  </Typography>
                  
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>Savings Consistency</b>
                      </Typography>
                      <Typography variant="body2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{account.performance.consistency_percentage}%</b>
                      </Typography>
                    </Box>
                    <PerformanceBar 
                      value={account.performance.consistency_percentage} 
                      sx={{ mt: 0.5, mb: 1 }}
                    />
                    <Typography variant="body2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{account.performance.weeks_with_deposits} of {account.performance.total_weeks} weeks active</b>
                    </Typography>
                  </Box>
                  
                  {accIndex < member.savings_performance.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))
            ) : (
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>No savings accounts found for this member.</b>
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Notes Section */}
      <Typography 
        sx={{ 
          textDecoration: 'underline',
          mt: 3,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} 
        variant="h6" 
        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
      >
        <b>Group Loan Notes</b>
      </Typography>
      
      {groupLoan.notes.length > 0 ? (
        groupLoan.notes.map((note, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography 
                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <b>{note.title}</b>
              </Typography>
              <Typography variant="body2" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{new Date(note.date).toLocaleDateString()} by {note.user_name}</b>
              </Typography>
            </Box>
            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
              <b>{note.note}</b>
            </Typography>
          </Paper>
        ))
      ) : (
        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
          <b>No notes available for this group loan.</b>
        </Typography>
      )}
    </Box>
  );
};


const MyTeams = () => {
    const [teams, setTeams] = useState([]);
    const [team_data, setTeam_data] = useState([]);
    const [show_one, setShow_one] = useState(false);
    const [show_one_one, setShow_one_one] = useState(false);
    const [in_what, setIn_what] = useState('');
    const [in_what_id, setIn_what_id] = useState(null);
    const [loan_data, setLoan_data] = useState([]);
    const [viewPerformance, setViewPerformance] = useState(false);

    const handleClosePerformance = () => {
      setViewPerformance(false);
    }

    const handleOpenDetails = (row) => {
        setLoan_data(row)
        setShow_one_one(true)
        formik.setFieldValue('request_id', row.id)
    }

    const formik = useFormik({
      initialValues: {
        request_id: null,
        amount_recommending: null, 
        note: "",
      },
    onSubmit: (values) => {
      const data = new FormData();
      data.append("id", values.request_id);
      data.append("team_id", in_what_id);
      data.append("note", values.note);
      if(!values.note){
        toast.error('Note is empty')
      }

      toast.promise(
          api
          .post(
            "/association/request/review",
            data,
            {
              headers: {
                Authorization: "Bearer " + fetchToken(),
                "Content-Type": "multipart/form-data",
              },
            }
          ),
          {
              loading: 'Processing request...',
              success: (response) => {
                getMyTeams();
                setShow_one_one(false);
                enterTeam(in_what_id);
                setLoan_data([]);
                formik.handleReset();

                return response.data
              },
              error: (error) => {
                  console.log(error);
                  
                  return 'Error, Please try again'
              }
          }
      )
    },
  });

    const enterTeam = (id) => {
      toast.promise(
        api.get(`/association/user/team/${id}`, {
          headers: {
            Authorization: "Bearer " + fetchToken(),
            "Content-Type": "multipart/form-data",
          },
        }),
        {
          loading: "Loading Your Team",
          success: (response) => {
            setTeam_data(response.data);
            setShow_one(true);
            setIn_what(response.data?.team_name);
            setIn_what_id(response.data?.team_id);
            
            return "Team Loaded Successfully";
          },
          error: (error) => {
            console.error(error);
            return 'Error, please try again';
          },
        }
      );
    }

    const getMyTeams = () => {
        toast.promise(
            api.get("/association/user/teams", {
              headers: {
                Authorization: "Bearer " + fetchToken(),
                "Content-Type": "multipart/form-data",
              },
            }),
            {
              loading: "Loading Your Teams",
              success: (response) => {
                setTeams(response.data?.my_teams);
                
                return "Teams Loaded Successfully";
              },
              error: (error) => {
                console.error(error);
                return 'Error, please try again';
              },
            }
          );
    }

    useEffect(() => {
        getMyTeams();
    },[])
  

  const currencyFormat = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
  });

  const pic = (pic) => {
    const de = decode(pic);
    return `data:image/jpeg;base64,${btoa(de)}`;
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: '90%', sm: '90%', md: 900 },
    maxHeight: "80vh",
    minHeight: "70vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "7px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
    bgcolor: "background.paper",
    borderRadius: "7px",
    boxShadow: 24,
    p: 4,
  };

  function formatDateToYearMonthDay(date) {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";
    return `${day}${suffix} ${month} ${year}`;
  }

  return (
    <div className="app">
      <main className="content">
        <Box sx={{ m: 5 }}>
          {/* HEADER */}
          <Box>
            <Typography
              variant="h3"
              sx={{ textAlign: "center", m: 1 }}
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
            >
              <b>My Teams</b>
            </Typography>
            <Typography
              variant="h6"
              sx={{ textAlign: "center", m: 0.5 }}
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
            >
              <b>{show_one?in_what:""}</b>
            </Typography>
          </Box>
          {/* Main Content */}
          <Grid container spacing={.1}>
            {
              show_one ? (
                <Chip
                  size="small"
                  label={
                    <Typography
                      variant="subtitle"
                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                    >
                      <b>Back to Teams</b>
                    </Typography>
                  }
                  icon={<KeyboardBackspaceRoundedIcon sx={{ color: "#fff" }} />}
                  sx={{
                    borderRadius: 2,
                    m: 1,
                    boxShadow:
                      "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#2b2d42",
                    color: "#fff",
                    "&:hover": {
                      boxShadow:
                        "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                      backgroundColor: "#14213d",
                    },
                  }}
                  onClick={() => setShow_one(false)}
                />
              ) : null
            }
            {
              !show_one ? (
                  <Grid item xs={12} md={12}>
                    <Box sx={{ display:'flex', flexDirection:'row', overflowX: "auto",
                        "&::-webkit-scrollbar": {
                        width: "7px",
                        },
                        "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                        background: "#555",
                    }, }}>
                        {
                          teams && teams.map((item) => (
                              <Box sx={{mx:3}}>
                                  <Item>
                                      <Box sx={{ display:'flex', flexDirection:'row', justifyContent:'space-between' }}>
                                          <Box sx={{ display:'flex', flexDirection:'column' }}>
                                              <Typography
                                                  variant="h5"
                                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                              >
                                                  <b>{item.name}</b>
                                              </Typography>
                                              <Typography
                                                  variant="caption"
                                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                              >
                                                  <b>Date Joined: {formatDateToYearMonthDay(new Date(item.date_assigned))}</b>
                                              </Typography>
                                          </Box>
                                          <Box sx={{ px: 5 }}></Box>
                                          <Box>
                                              <Button
                                                  size="small"
                                                  color="success"
                                                  variant="contained"
                                                  sx={{
                                                      borderRadius: 2,
                                                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                                      "&:hover": {
                                                      boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                      backgroundColor: "#14213d",
                                                      },
                                                  }}
                                                  onClick={() => enterTeam(item.id)}
                                              >
                                                  <Typography
                                                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                                  >
                                                      <b>Enter</b>
                                                  </Typography>
                                              </Button>
                                          </Box>
                                      </Box>
                                  </Item>
                              </Box>
                          ))
                      }
                    </Box>
                </Grid>
              ) : (
                <Grid item xs={12} md={12}>
                  <Grid container spacing={2}>
                      {/* list of things */}
                    <Grid item xs={12} md={8}>
                      <Item>
                        
                        <Typography
                          variant="h5"
                          sx={{ textAlign: "center", m: 1 }}
                          fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                        >
                          <b>Active Requests</b>
                        </Typography>
                        <Divider variant="middle" sx={{my:4}}>
                          <Typography
                              variant="caption"
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                          >
                              <b>Groups Loan Requests</b>
                          </Typography>
                        </Divider>
                        <Box sx={{ display:'flex', flexDirection:'column' }}>
                          <Box sx={{ display:'flex', flexDirection:'row', overflowX: "auto",
                              "&::-webkit-scrollbar": {
                              width: "7px",
                              },
                              "&::-webkit-scrollbar-track": {
                              background: "#f1f1f1",
                              },
                              "&::-webkit-scrollbar-thumb": {
                              background: "#888",
                              borderRadius: "10px",
                              },
                              "&::-webkit-scrollbar-thumb:hover": {
                              background: "#555",
                          }, }}>
                            {
                              team_data && team_data?.group_loans?.map((item) => (
                                  <Box sx={{mx:3}}>
                                    <Item>
                                        <Box sx={{ display:'flex', flexDirection:'row', justifyContent:'space-between' }}>
                                            <Box sx={{ display:'flex', flexDirection:'column' }}>
                                                <Typography
                                                    variant="h5"
                                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                                >
                                                    <b>{item.group_name}</b>
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                                >
                                                    <b>Savings Group: {item.association_name}</b>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ px: 5 }}></Box>
                                            <Box>
                                                <Button
                                                    size="small"
                                                    color="success"
                                                    variant="contained"
                                                    sx={{
                                                        borderRadius: 2,
                                                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                                        "&:hover": {
                                                        boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                        backgroundColor: "#14213d",
                                                        },
                                                    }}
                                                    onClick={() => handleOpenDetails(item)}
                                                >
                                                    <FileOpenRoundedIcon />
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Item>
                                </Box>
                              ))
                            }
                          </Box>
                        </Box>

                        <Divider variant="middle">
                          <Typography
                              variant="caption"
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                          >
                              <b>Individual Loans Requests</b>
                          </Typography>
                        </Divider>
                        <Box
                                sx={{
                                overflow: "auto",
                                marginBottom: 10,
                                maxHeight: 340,
                                "&::-webkit-scrollbar": {
                                    width: "7px",
                                    height: "7px",
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "#888",
                                    borderRadius: "10px",
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#555",
                                },
                                }}
                            >
                                {!team_data?.loans ? (
                                <Typography
                                    sx={{ m: 1 }}
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                    <b>No Individual Loans Available</b>
                                </Typography>
                                ) : null}

                                <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>No.</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Request Date</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Loan Product</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Amount</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Member</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Savings Group</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Group Name</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    {/* <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Cluster</b>
                                        </Typography>{" "}
                                    </TableCell> */}
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Open</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {team_data?.loans?.map((row, index) => (
                                    <TableRow
                                        key={row.index}
                                        sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                        backgroundColor: "#f5f3f4",
                                        }}
                                    >
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{index + 1}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatDateToYearMonthDay(new Date(row.transaction_date))}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.account_name}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{currencyFormat.format(row.recommended_amount? row.recommended_amount :row.amount)}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.member_name}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.association_name}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.group_name}</b>
                                        </Typography>
                                        </TableCell>
                                        {/* <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.cluster_name}</b>
                                        </Typography>
                                        </TableCell> */}
                                        <TableCell align="left">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <Button
                                                  size="small"
                                                  color="success"
                                                  variant="contained"
                                                  sx={{
                                                  borderRadius: 2,
                                                  boxShadow:
                                                      "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                                  "&:hover": {
                                                      boxShadow:
                                                      "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                      backgroundColor: "#14213d",
                                                  },
                                                  }}
                                                  onClick={() => handleOpenDetails(row)}
                                              >
                                                  <FileOpenRoundedIcon />
                                              </Button>
                                          </Typography>
                                        </TableCell>
                                        
                                    </TableRow>
                                    ))}
                                </TableBody>
                                </Table>
                            </Box>

                            <Divider variant="middle">
                          <Typography
                              variant="caption"
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                          >
                              <b>Expense Requests</b>
                          </Typography>
                        </Divider>
                        <Box
                                sx={{
                                overflow: "auto",
                                marginBottom: 10,
                                maxHeight: 340,
                                "&::-webkit-scrollbar": {
                                    width: "7px",
                                    height: "7px",
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "#888",
                                    borderRadius: "10px",
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#555",
                                },
                                }}
                            >
                                {!team_data?.expenses ? (
                                <Typography
                                    sx={{ m: 1 }}
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                    <b>No Expense Request Available</b>
                                </Typography>
                                ) : null}

                                <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>No.</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Request Date</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Purpose</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Amount</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Narration</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>User Name</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Open</b>
                                        </Typography>{" "}
                                    </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {team_data?.expenses?.map((row, index) => (
                                    <TableRow
                                        key={row.index}
                                        sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                        backgroundColor: "#f5f3f4",
                                        }}
                                    >
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{index + 1}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatDateToYearMonthDay(new Date(row.request_date))}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.ledger}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{currencyFormat.format(row.recommended_amount? row.recommended_amount :row.amount)}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.narration}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{row.user_name}</b>
                                        </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <Button
                                                  size="small"
                                                  color="success"
                                                  variant="contained"
                                                  sx={{
                                                  borderRadius: 2,
                                                  boxShadow:
                                                      "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                                  "&:hover": {
                                                      boxShadow:
                                                      "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                      backgroundColor: "#14213d",
                                                  },
                                                  }}
                                                  onClick={() => handleOpenDetails(row)}
                                              >
                                                  <FileOpenRoundedIcon />
                                              </Button>
                                          </Typography>
                                        </TableCell>
                                        
                                    </TableRow>
                                    ))}
                                </TableBody>
                                </Table>
                            </Box>
                      </Item>
                     
                    </Grid>
                    {/* Details Board */}
                    <Grid item xs={12} md={4}>
                      <Item>
                        <Box sx={{ display:'flex', flexDirection:'row', overflowX: "auto",
                            "&::-webkit-scrollbar": {
                            height: "7px",
                            },
                            "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            },
                            "&::-webkit-scrollbar-thumb": {
                            background: "#888",
                            borderRadius: "10px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                            background: "#555",
                        }, }}>
                          {
                            team_data && team_data?.team_members.map((item) => (
                              <Box sx={{ display:'flex', flexDirection:'column', m:2 }}>
                                <Avatar
                                  alt={item.name}
                                  src={pic(item.memberImage)}
                                  sx={{ width: 70, height: 70 }}
                                />
                                <Typography
                                  variant="h5"
                                  sx={{ textAlign:'center' }}
                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                  <b>{item.name}</b>
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{ textAlign:'center' }}
                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                  <b>{item.role}</b>
                                </Typography>
                              </Box>
                            ))
                          }
                        </Box>
                          <Divider variant='middle' sx={{ my:3 }}/>
                          <Typography
                              variant="h4"
                              sx={{ textAlign: "center", m: 1 }}
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                          >
                              <b>Loan Details</b>
                          </Typography>
                    {
                        !show_one_one &&
                            <Typography
                                variant="caption"
                                sx={{ textAlign: "center" }}
                                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                            >
                                <b>Open Group/Individual Loan Or Expense Request To View Details</b>
                            </Typography>
                    }
                    <Divider variant='middle' sx={{ my:3 }}/>
                    {
                        loan_data.request_type === "Loan"?
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Requested Amount: {currencyFormat.format(loan_data.amount)}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Recommended Amount: {currencyFormat.format(loan_data.recommended_amount)}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Request Date: {formatDateToYearMonthDay(new Date(loan_data.transaction_date))}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Loan Product: {loan_data.account_name}</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Cluster: {loan_data.cluster_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Community: {loan_data.community_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Savings Group: {loan_data.association_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Member Name: {loan_data.member_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Member Phone: {loan_data.member_phone}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Passbook No.: {loan_data.passbook}</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>
                                
                                <Grid item xs={12} md={12}>
                                  <Button
                                      size="small"
                                      color="success"
                                      variant="contained"
                                      sx={{
                                      borderRadius: 2,
                                      m: 1,
                                      boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                          boxShadow:
                                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                          backgroundColor: "#14213d",
                                      },
                                      }}
                                      onClick={() => setViewPerformance(true)}
                                  >
                                    View Performance Data
                                  </Button>
                                </Grid>
                                
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                  <Box  
                                    sx={{ 
                                      display:'flex', 
                                      flexDirection:'column', 
                                      overflowY: "auto",
                                        "&::-webkit-scrollbar": {
                                          height: "7px",
                                        },
                                        "&::-webkit-scrollbar-track": {
                                          background: "#f1f1f1",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                          background: "#888",
                                          borderRadius: "10px",
                                        },
                                        "&::-webkit-scrollbar-thumb:hover": {
                                          background: "#555",
                                        },
                                    }}
                                  >
                                    {
                                      loan_data?.notes[0]?
                                        loan_data?.notes.map((n) => (
                                          <Box sx={{ display:'flex', flexDirection:'column', width:'100%'}}>
                                            <Box sx={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
                                              <Typography 
                                                sx={{
                                                  textDecoration: 'underline',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  '&::before': {
                                                    content: '"•"',
                                                    marginRight: '8px'
                                                  }
                                                }} 
                                                variant="title" 
                                                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                              >
                                                <b>{n.title}</b>
                                              </Typography>
                                              <Typography variant="subtitle" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatDateToYearMonthDay(new Date(n.date))}</b>
                                              </Typography>
                                            </Box>
                                            <Typography variant="text" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                              <b>{n.user_name}: {n.note}</b>
                                            </Typography>
                                          </Box>
                                        ))
                                      :
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                          <b>No Notes Attached</b>
                                      </Typography>
                                    }
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        sx={{ m:1 }}
                                        size="small"
                                        required
                                        name="note"
                                        type='text'
                                        multiline
                                        minRows={4}
                                        value={formik.values.note}
                                        onChange={formik.handleChange}
                                        placeholder="Note here..."
                                        fullWidth
                                    />
                                    
                                    <Box sx={{ display:'flex', flexDirection:'row', mt:'auto' }}>
                                        {
                                          loan_data.complete?
                                          <Button
                                            size="small"
                                            color="info"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            disabled={loan_data.complete}
                                          >
                                            Review Completed
                                          </Button>
                                          :
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            onClick={formik.handleReset}
                                        >
                                            Cancel
                                        </Button>
                                        }
                                        

                                        <Button
                                            size="small"
                                            color="success"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            onClick={formik.handleSubmit}
                                            disabled={loan_data.complete}
                                        >
                                          {loan_data.complete? 'Waiting for others...': 'Complete Review'}
                                        </Button>
                                    </Box>
                                    </Grid>
                            </Grid>
                        : loan_data.request_type === "Group Loan"?
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Requesting Amount: {currencyFormat.format(loan_data.total_amount)}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Build Date: {formatDateToYearMonthDay(new Date(loan_data.build_date))}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Loan Product: {loan_data.account_name}</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Cluster: {loan_data.cluster_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Community: {loan_data.community_name}</b>
                                    </Typography>
                                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Savings Group: {loan_data.association_name}</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Typography variant="h4" sx={{ textAlign:'center', m:1 }} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>Review Group Loan</b>
                                    </Typography>
                                    <Typography sx={{color:'red'}} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>NB: Make Sure All Requests In Group Is Reviewed Before Processing The This</b>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                  <Button
                                      size="small"
                                      color="success"
                                      variant="contained"
                                      sx={{
                                      borderRadius: 2,
                                      m: 1,
                                      boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                          boxShadow:
                                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                          backgroundColor: "#14213d",
                                      },
                                      }}
                                      onClick={() => setViewPerformance(true)}
                                  >
                                    View Performance Data
                                  </Button>
                                </Grid>
                                
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                  <Box  
                                    sx={{ 
                                      display:'flex', 
                                      flexDirection:'column', 
                                      overflowY: "auto",
                                        "&::-webkit-scrollbar": {
                                          height: "7px",
                                        },
                                        "&::-webkit-scrollbar-track": {
                                          background: "#f1f1f1",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                          background: "#888",
                                          borderRadius: "10px",
                                        },
                                        "&::-webkit-scrollbar-thumb:hover": {
                                          background: "#555",
                                        },
                                    }}
                                  >
                                    {
                                      loan_data?.notes[0]?
                                        loan_data?.notes.map((n) => (
                                          <Box sx={{ display:'flex', flexDirection:'column', width:'100%'}}>
                                            <Box sx={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
                                              <Typography 
                                                sx={{
                                                  textDecoration: 'underline',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  '&::before': {
                                                    content: '"•"',
                                                    marginRight: '8px'
                                                  }
                                                }} 
                                                variant="title" 
                                                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                              >
                                                <b>{n.title}</b>
                                              </Typography>
                                              <Typography variant="subtitle" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatDateToYearMonthDay(new Date(n.date))}</b>
                                              </Typography>
                                            </Box>
                                            <Typography variant="text" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                              <b>{n.user_name}: {n.note}</b>
                                            </Typography>
                                          </Box>
                                        ))
                                      :
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                          <b>No Notes Attached</b>
                                      </Typography>
                                    }
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Divider variant="middle" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        sx={{ m:1 }}
                                        size="small"
                                        required
                                        name="note"
                                        type='text'
                                        multiline
                                        minRows={4}
                                        value={formik.values.note}
                                        onChange={formik.handleChange}
                                        placeholder="Note here..."
                                        fullWidth
                                    />
                                    
                                    <Box sx={{ display:'flex', flexDirection:'row', mt:'auto' }}>
                                        {
                                          loan_data.complete?
                                          <Button
                                            size="small"
                                            color="info"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            disabled={loan_data.complete}
                                          >
                                            Review Completed
                                          </Button>
                                          :
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            onClick={formik.handleReset}
                                        >
                                            Cancel
                                        </Button>
                                        }
                                        

                                        <Button
                                            size="small"
                                            color="success"
                                            variant="contained"
                                            sx={{
                                            borderRadius: 2,
                                            m: 1,
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                            "&:hover": {
                                                boxShadow:
                                                "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "#14213d",
                                            },
                                            }}
                                            onClick={formik.handleSubmit}
                                            disabled={loan_data.complete}
                                        >
                                          {loan_data.complete? 'Waiting for others...': 'Complete Review'}
                                        </Button>
                                    </Box>
                                    </Grid>
                                
                        </Grid>
                        : loan_data.request_type === "Expense"?  
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Requested Amount: {currencyFormat.format(loan_data.amount)}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Recommended Amount: {currencyFormat.format(loan_data.recommended_amount)}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Request Date: {formatDateToYearMonthDay(new Date(loan_data.request_date))}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Purpose: {loan_data.ledger}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>User Narration: {loan_data.narration}</b>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>User Name: {loan_data.user_name}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>User Phone: {loan_data.user_phone}</b>
                            </Typography>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Role: {loan_data.user_role}</b>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={12}>
                              <Divider variant="middle" />
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <Box  
                              sx={{ 
                                display:'flex', 
                                flexDirection:'column', 
                                overflowY: "auto",
                                  "&::-webkit-scrollbar": {
                                    height: "7px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1",
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    background: "#888",
                                    borderRadius: "10px",
                                  },
                                  "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#555",
                                  },
                              }}
                            >
                              {
                                loan_data?.notes[0]?
                                  loan_data?.notes.map((n) => (
                                    <Box sx={{ display:'flex', flexDirection:'column', width:'100%'}}>
                                      <Box sx={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
                                        <Typography 
                                          sx={{
                                            textDecoration: 'underline',
                                            display: 'flex',
                                            alignItems: 'center',
                                            '&::before': {
                                              content: '"•"',
                                              marginRight: '8px'
                                            }
                                          }} 
                                          variant="title" 
                                          fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                        >
                                          <b>{n.title}</b>
                                        </Typography>
                                        <Typography variant="subtitle" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                          <b>{formatDateToYearMonthDay(new Date(n.date))}</b>
                                        </Typography>
                                      </Box>
                                      <Typography variant="text" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{n.user_name}: {n.note}</b>
                                      </Typography>
                                    </Box>
                                  ))
                                :
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>No Notes Attached</b>
                                </Typography>
                              }
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={12}>
                              <Divider variant="middle" />
                          </Grid>
                          <Grid item xs={12} md={12}>
                              <TextField
                                  sx={{ m:1 }}
                                  size="small"
                                  required
                                  name="note"
                                  type='text'
                                  multiline
                                  minRows={4}
                                  value={formik.values.note}
                                  onChange={formik.handleChange}
                                  placeholder="Note here..."
                                  fullWidth
                              />
                              
                              <Box sx={{ display:'flex', flexDirection:'row', mt:'auto' }}>
                                  {
                                    loan_data.complete?
                                    <Button
                                      size="small"
                                      color="info"
                                      variant="contained"
                                      sx={{
                                      borderRadius: 2,
                                      m: 1,
                                      boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                          boxShadow:
                                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                          backgroundColor: "#14213d",
                                      },
                                      }}
                                      disabled={loan_data.complete}
                                    >
                                      Review Completed
                                    </Button>
                                    :
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="contained"
                                      sx={{
                                      borderRadius: 2,
                                      m: 1,
                                      boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                          boxShadow:
                                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                          backgroundColor: "#14213d",
                                      },
                                      }}
                                      onClick={formik.handleReset}
                                  >
                                      Cancel
                                  </Button>
                                  }
                                  

                                  <Button
                                      size="small"
                                      color="success"
                                      variant="contained"
                                      sx={{
                                      borderRadius: 2,
                                      m: 1,
                                      boxShadow:
                                          "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                          boxShadow:
                                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                          backgroundColor: "#14213d",
                                      },
                                      }}
                                      onClick={formik.handleSubmit}
                                      disabled={loan_data.complete}
                                  >
                                    {loan_data.complete? 'Waiting for others...': 'Complete Review'}
                                  </Button>
                              </Box>
                            </Grid>
                        </Grid>
                        :null
                    }

                      </Item>
                    </Grid>
                  </Grid>
                </Grid>
              )
            }
              
            
          </Grid>
          <Modal
            open={viewPerformance}
            onClose={handleClosePerformance}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={12}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      mb: 2,
                      textAlign: 'center',
                      color: '#2c3e50'
                    }}
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Performance Overview</b>
                  </Typography>
                </Grid>
                {
                  loan_data.request_type === "Loan"?
                  <Grid item xs={12} md={12}>
                  <Box sx={{ 
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2,
                        textAlign: 'center',
                        color: '#2c3e50'
                      }}
                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                    >
                      <b>Savings Accounts</b>
                    </Typography>
                    
                    {loan_data?.savings_account_list?.length > 0 ? (
                      <Grid container spacing={2}>
                        {loan_data.savings_account_list.map((account, index) => (
                          <Grid item xs={12} key={index}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1
                              }}>
                                <Typography 
                                  variant="h6"
                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                  <b>{account.account_name}</b>
                                </Typography>
                                <Typography 
                                  variant="h6"
                                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                >
                                  <b>{currencyFormat.format(account.current_balance)}</b>
                                </Typography>
                              </Box>
                              
                              {/* Savings Performance */}
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  mb: 1
                                }}>
                                  <Typography 
                                    variant="body1"
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                  >
                                    <b>Savings Consistency:</b>
                                  </Typography>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center'
                                  }}>
                                    <CircularProgressWithLabel 
                                      value={account.savings_performance.consistency_percentage}
                                      performance={account.savings_performance.performance_rating}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={{ ml: 1 }}
                                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                    >
                                      <b>{account.savings_performance.performance_rating}</b>
                                    </Typography>
                                  </Box>
                                </Box>
                                
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  mt: 1
                                }}>
                                  <Typography 
                                    variant="caption"
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                  >
                                    <b>Weeks with deposits: {account.savings_performance.weeks_with_deposits}</b>
                                  </Typography>
                                  <Typography 
                                    variant="caption"
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                  >
                                    <b>Total weeks: {account.savings_performance.total_weeks}</b>
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography 
                        variant="body1"
                        sx={{ textAlign: 'center', fontStyle: 'italic' }}
                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                      >
                        <b>No savings accounts found</b>
                      </Typography>
                    )}
                  </Box>
                </Grid>
                  :null
                }
                {
                  loan_data.request_type === "Group Loan"?
                    <Grid item xs={12} md={12}>
                      <GroupLoanPerformance groupLoan={loan_data} />
                    </Grid>
                  :null
                }
                {
                  loan_data.request_type === "Expense"?
                    <Grid item xs={12} md={12}>
                      
                    </Grid>
                  :null
                }
              </Grid>
            </Box>
          </Modal>
        </Box>
      </main>
    </div>
  );
};

export default MyTeams;